import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import DateBox from 'devextreme-react/date-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import TextBox from 'devextreme-react/text-box';
import DataGrid, { Column, Editing, Toolbar, Item } from 'devextreme-react/data-grid';
import EditSalaryPopup from './EditSalaryPopup';
import './App.css';

const API_BASE = "https://qbo.omnivistaweb.com";

interface SalaryDetail {
  pay_detail_id: number;
  item_id: string;
  amount: number;
  notes?: string;
  temp_id: string;
}

interface SalaryRow {
  employee_id: string;
  start_date: string;
  pay_detail_id: number;
  pay_type: string;
  details: SalaryDetail[];
}

const EmployeeDashboard = () => {
  const [employees, setEmployees] = useState<string[]>([]);
  const [employeeDetails, setEmployeeDetails] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editFields, setEditFields] = useState<any>({});
  const [showDetails, setShowDetails] = useState(true);
  const [randomSalaryData, setRandomSalaryData] = useState(false);
  const [tab, setTab] = useState<'details' | 'salary' | 'bank'>('details');
  

  

  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  const [startDate, setStartDate] = useState<Date | undefined>(oneYearAgo);
  const [endDate, setEndDate] = useState<Date | undefined>(today);

  /*Employee Salary */
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [salaryRows, setSalaryRows] = useState<SalaryRow[]>([]);
  const [expenseItems, setExpenseItems] = useState<{ name: string; value: string }[]>([]);
  const [payTypes, setPayTypes] = useState<{ name: string; value: string }[]>([]);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [editData, setEditData] = useState<SalaryRow | null>(null);
  const [randomMode, setRandomMode] = useState<boolean>(false);

  const gridRef = useRef<any>(null);
  const detailGridRef = useRef<any>(null);

  useEffect(() => {
    document.body.classList.add('scroll-lock');
  
    return () => {
      document.body.classList.remove('scroll-lock');
    };
  }, []);


  useEffect(() => {
    axios.get(`${API_BASE}/api/employees`).then((res) => {
      const sorted = (res.data || []).sort((a: string, b: string) => a.localeCompare(b));
      setEmployees(sorted);
    }).catch(() => {
      setError('Failed to load employees');
    });

    axios.get(`${API_BASE}/api/employees/full`).then((res) => {
      setEmployeeDetails(res.data || []);
    });

    axios.get(`${API_BASE}/api/settings/qbo`).then(res => {
      setRandomSalaryData(!!res.data.qbo_random_salary_data);
    }).catch(() => {
      console.warn('⚠️ Failed to fetch QBO settings.');
    });

    /* Employee Salary */
    axios.get(`${API_BASE}/api/accounts/expenses?includeParents=true`).then(res => {
      setExpenseItems(res.data.map((item: any) => ({
        name: item.parent_name ? `${item.parent_name}:${item.name}` : item.name,
        value: item.value
      })));
    });

    axios.get(`${API_BASE}/api/employees/pay-types`).then(res => {
      setPayTypes(res.data.map((payType: any) => ({
        name: payType.type,
        value: payType.type
      })));
    });

  }, []);

  
  const handleSelect = (name: string) => {
    console.log("👤 Selected employee:", name);
    setSelected(name);
    setRows([]);
    setEditFields({});
    setError('');
    const emp = employeeDetails.find(e => e.display_name === name);
    if (emp) {
      setEditFields({
        employee_name: emp.employee_name || '',
        unique_employee_id: emp.unique_employee_id || '',
        employee_email: emp.employee_email || '',
        bank_code: emp.bank_code || '',
        iban: emp.iban || '',
        hire_date: emp.hire_date ? new Date(emp.hire_date) : null,
        termination_date: emp.termination_date ? new Date(emp.termination_date) : null,
        last_basic_salary: emp.last_basic_salary || 0
      });      
    };
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_BASE}/api/employees/update`, {
        display_name: selected,
        updates: editFields
      });
      alert('✅ Employee info updated');
    } catch (err) {
      console.error(err);
      alert('❌ Failed to update employee');
    }
  };

  /* Employee Salary Handlers */
  const handleSalarySelect = async (name: string) => {
    setSelectedEmployee(name);
    const res = await axios.get(`${API_BASE}/api/employees/salary`, { params: { name } });
    const withTempIds = res.data.map((row: SalaryRow) => ({
      ...row,
      details: row.details.map((d: any, idx: number) => ({
        ...d,
        temp_id: `${row.pay_detail_id}_${idx + 1}`
      }))
    }));
    setSalaryRows(withTempIds);
    setSelectedRow(withTempIds.length > 0 ? withTempIds[0].pay_detail_id : null);
  };

  const handleSalaryAddRow = async () => {
    if (!selectedEmployee || randomMode) return;

    const res = await axios.get(`${API_BASE}/api/employees/pay-detail-id/next`);
    const newRow: SalaryRow = {
      employee_id: selectedEmployee,
      pay_detail_id: res.data.next,
      start_date: new Date().toISOString().split('T')[0],
      pay_type: '',
      details: []
    };
    setEditData(newRow);
  };

  const handleSalarySave = () => {
    if (randomMode) return;

    const sanitized = salaryRows.map(row => ({
      ...row,
      details: row.details.map(({ temp_id, ...rest }) => rest)
    }));

    axios.post(`${API_BASE}/api/employees/salary/save`, { name: selectedEmployee, rows: sanitized })
      .then(() => alert('✅ Salary data saved!'))
      .catch(() => alert('❌ Failed to save'));
  };

  const handleSalaryDeleteRow = (pay_detail_id: number) => {
    if (randomMode) return;
    setSalaryRows(prev => prev.filter(r => r.pay_detail_id !== pay_detail_id));
  };

  const selectedDetails = salaryRows.find(r => r.pay_detail_id === selectedRow)?.details || [];
  const totalAmount = selectedDetails.reduce((sum, d) => sum + (d.amount || 0), 0);

  const fetchData = async () => {
    if (!selected) {
      setError('Please select an employee.');
      return;
    }

    setLoading(true);
    console.log("📤 Fetching data for:", selected);
    try {
      const empResp = await axios.get(`${API_BASE}/api/employees/full`);
      setEmployeeDetails(empResp.data || []);
      const matched = empResp.data.find((e: any) => e.display_name === selected);
      if (matched) {
        setEditFields({
          employee_name: matched.employee_name || '',
          unique_employee_id: matched.unique_employee_id || '',
          employee_email: matched.employee_email || '',
          bank_code: matched.bank_code || '',
          iban: matched.iban || '',
          hire_date: matched.hire_date ? new Date(matched.hire_date) : null,
          termination_date: matched.termination_date ? new Date(matched.termination_date) : null,
          last_basic_salary: matched.last_basic_salary || 0
        });        
      }

      const res = await axios.get(`${API_BASE}/api/employees/history`, {
        params: {
          name: selected,
          start: startDate?.toISOString().split('T')[0],
          end: endDate?.toISOString().split('T')[0]
        }
      });

      console.log("📥 History API response:", res.data);
      setRows(res.data);
      setError('');
    } catch (err) {
      console.error('API error:', err);
      setError('Failed to load payroll data.');
    }
    setLoading(false);
  };

  const formatCurrency = (val: number) =>
    val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const groupByTxnId = (items: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    for (const row of items) {
      const key = row.txn_id;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    }
    return grouped;
  };

  const grouped = groupByTxnId(rows);
  const sortedGroups = Object.entries(grouped).sort((a, b) => new Date(b[1][0].date).getTime() - new Date(a[1][0].date).getTime());

  return (
    <div className="dashboard-wrapper" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: '0 0 auto', position: 'sticky', top: 0, zIndex: 1000 }}>
        <div style={{ background: 'transparent', padding: '16px 16px 0' }}>
          <div className="dashboard-controls" style={{ background: '#fff', padding: 16, borderRadius: 8 }}>
            <div className="dashboard-row" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <SelectBox
                items={employees}
                value={selected}
                onValueChanged={(e) => {
                  handleSelect(e.value);
                  handleSalarySelect(e.value);
                }}
                placeholder="Select an employee"
                searchEnabled
                width={300}
              />
              <DateBox
                placeholder="Start date"
                value={startDate}
                onValueChanged={(e) => setStartDate(e.value)}
                width={150}
              />
              <DateBox
                placeholder="End date"
                value={endDate}
                onValueChanged={(e) => setEndDate(e.value)}
                width={150}
              />
              <Button
                text="Apply"
                type="default"
                stylingMode="contained"
                onClick={fetchData}
                style={{ backgroundColor: 'var(--blue-primary)', color: '#fff' }}
              />
            </div>
          </div>

          {/* WRAP BOTH LEFT + RIGHT SIDES ALWAYS */}
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
              
            {/* Left-Begin - Employee Details */}
            <div style={{ flex: '1 1 50%', background: '#fff', padding: 16, borderRadius: 8,marginTop: 12 }}>
              {rows.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => setShowDetails(!showDetails)}>☰</span>
                    <h4 style={{ margin: 0, fontWeight: 600, color: '#1e1b4b' }}>Employee Details</h4>
                  </div>
                  {showDetails && (
                    <>
                      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <Button text="Details" onClick={() => setTab('details')} stylingMode={tab === 'details' ? 'contained' : 'outlined'} />
                        <Button text="Salary Info" onClick={() => setTab('salary')} stylingMode={tab === 'salary' ? 'contained' : 'outlined'} />
                        <Button text="Bank Info" onClick={() => setTab('bank')} stylingMode={tab === 'bank' ? 'contained' : 'outlined'} />
                      </div>

                      {tab === 'details' && (
                        <div className="box">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                            <TextBox value={editFields.employee_name} onValueChanged={e => setEditFields({ ...editFields, employee_name: e.value })} label="Employee Name" width={250} />
                            <TextBox value={editFields.unique_employee_id} onValueChanged={e => setEditFields({ ...editFields, unique_employee_id: e.value })} label="Employee ID" width={180} />
                            <TextBox value={editFields.employee_email} onValueChanged={e => setEditFields({ ...editFields, employee_email: e.value })} label="Employee Email" width={250} />
                            <DateBox label="Hire Date" value={editFields.hire_date} onValueChanged={e => setEditFields({ ...editFields, hire_date: e.value })} width={180} />
                            <DateBox label="Termination Date" value={editFields.termination_date} onValueChanged={e => setEditFields({ ...editFields, termination_date: e.value })} width={180} />
                            <TextBox
                              label="Last Basic Salary"
                              value={editFields.last_basic_salary}
                              inputAttr={{ type: 'number' }}
                              disabled={randomSalaryData}
                              onValueChanged={e => setEditFields({ ...editFields, last_basic_salary: parseFloat(e.value || 0) })}
                              width={180}
                            />
                          </div>
                        </div>
                      )}

                      {tab === 'salary' && (
                        <div className="box">
                          <DataGrid
                            className="salary-grid"
                            ref={gridRef}
                            dataSource={salaryRows}
                            keyExpr="pay_detail_id"
                            showBorders
                            focusedRowEnabled
                            focusedRowKey={selectedRow}
                            onFocusedRowChanged={(e) => setSelectedRow(e.row?.data?.pay_detail_id || null)}
                            disabled={randomMode}
                          >
                            <Editing allowUpdating allowDeleting={!randomMode} mode="row" useIcons />
                            <Toolbar>
                              <Item location="before">
                                <Button icon="add" text="Add" type="default" stylingMode="contained" onClick={handleSalaryAddRow} />
                              </Item>
                              <Item location="before">
                                <Button icon="save" text="Save" type="success" stylingMode="contained" onClick={() => {
                                  handleSave();
                                  handleSalarySave();
                                }} />
                              </Item>
                            </Toolbar>
                            <Column dataField="employee_id" caption="Employee ID" allowEditing={false} visible={false} />
                            <Column dataField="start_date" caption="Start Date" dataType="date" />
                            <Column
                              dataField="pay_type"
                              caption="Type"
                              lookup={{
                                dataSource: payTypes,
                                valueExpr: 'value',
                                displayExpr: 'name'
                              }}
                            />
                            <Column
                              caption="Total Amount"
                              dataType="number"
                              alignment="right"
                              format={{ type: 'fixedPoint', precision: 2 }}
                              calculateCellValue={(rowData: SalaryRow) =>
                                (rowData.details || []).reduce((sum, d) => sum + (d.amount || 0), 0)
                              }
                            />
                            <Column
                              type="buttons"
                              width={140}
                              buttons={[
                                {
                                  icon: 'edit',
                                  onClick: (e: any) => { if (!randomMode) setEditData(e.row.data); }
                                },
                                {
                                  icon: 'trash',
                                  onClick: (e: any) => handleSalaryDeleteRow(e.row.data.pay_detail_id)
                                }
                              ]}
                            />
                          </DataGrid>
                          
                          {editData && (
                            <EditSalaryPopup
                              data={editData}
                              onClose={(updated) => {
                                if (updated) {
                                  const idx = salaryRows.findIndex(r => r.pay_detail_id === updated.pay_detail_id);
                                  const rows = [...salaryRows];
                                  if (idx > -1) rows[idx] = updated;
                                  else rows.push(updated);
                                  setSalaryRows(rows);
                                }
                                setEditData(null);
                              }}
                              expenseItems={expenseItems}
                              payTypes={payTypes}
                              randomMode={randomMode}
                            />
                          )}
                          </div>
                      )}
                    
                      {tab === 'bank' && (
                        <div className="box">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                            <TextBox value={editFields.bank_code} onValueChanged={e => setEditFields({ ...editFields, bank_code: e.value })} label="Bank Code" width={150} />
                            <TextBox value={editFields.iban} onValueChanged={e => setEditFields({ ...editFields, iban: e.value })} label="IBAN" width={300} />
                          </div>
                        </div>
                      )}

                      <Button text="Save" onClick={handleSave} style={{ marginTop: 16, backgroundColor: 'var(--blue-primary)', color: 'white' }} />
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Left-End: Employee Details */}

            {/* RIGHT SIDE - Paychecks */}
            <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Right-Begin: Latest paycheck  */}
              {!loading && sortedGroups.length > 0 && (
                <div style={{ background: 'var(--light-green-bg)', padding: 20, borderRadius: 8, marginTop: 16 }}>
                  <h2 style={{ margin: 0 }}>Latest paycheck</h2>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <div style={{ fontSize: 28, fontWeight: 'bold', color: 'var(--green-primary)' }}>
                      AED {formatCurrency(sortedGroups[0][1].reduce((sum, entry) => sum + (entry.amount || 0), 0))}
                    </div>
                    <div style={{ fontSize: 28, color: 'var(--green-primary)' }}>
                      {sortedGroups[0][1][0]?.date || ''}
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Button
                      text="View details"
                      onClick={() => {
                        const txnId = sortedGroups[0][0];
                        const employeeId = sortedGroups[0][1][0]?.unique_employee_id || txnId;
                        const paidDate = sortedGroups[0][1][0]?.date;
                        const key = `paystub-${employeeId}`;
                        localStorage.setItem(key, JSON.stringify({ data: sortedGroups[0][1], start: paidDate, end: paidDate }));
                        console.log("🔍 Opening paystub for txnId:", txnId);
                        window.open(`/paystub/${txnId}`, '_blank');
                      }}
                      style={{ backgroundColor: 'var(--blue-primary)', color: '#fff' }}
                    />
                  </div>
                </div>
              )}
              {/* Right-End: Latest paycheck  */}

              {/* Right-Begin: Previous paycheck  */}
              <div style={{ flex: '1 1 auto', overflowY: 'auto', paddingTop: 16 }}>
                {loading && <div style={{ textAlign: 'center', margin: '2rem' }}><LoadIndicator /></div>}

                {!loading && sortedGroups.length > 1 && <h3 style={{ margin: '16px 16px' }}>Previous Paychecks</h3>}

                {!loading && sortedGroups.slice(1).map(([txnId, entries]) => {
                  const total = entries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
                  const employeeId = entries[0]?.unique_employee_id || txnId;
                  const paidDate = entries[0]?.date || '';

                  return (
                    <div key={txnId} style={{ background: '#fff', marginBottom: 16, padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginLeft: 16, marginRight: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--grey-text)' }}>Paid on</div>
                          <div style={{ fontSize: 18, fontWeight: 'bold' }}>{paidDate}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 12, color: 'var(--grey-text)', textAlign: 'right' }}>Net Pay</div>
                          <div style={{ fontSize: 18, fontWeight: 'bold', color: 'var(--green-primary)', textAlign: 'right' }}>AED {formatCurrency(total)}</div>
                        </div>
                        <div>
                          <Button
                            icon="download"
                            hint="Download Paystub"
                            onClick={() => {
                              console.log("⬇️ Download Paystub txnId:", txnId);
                              const key = `paystub-${employeeId}`;
                              localStorage.setItem(key, JSON.stringify({ data: entries, start: paidDate, end: paidDate }));
                              window.open(`/paystub/${txnId}`, '_blank');
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {error && <div className="error-message">{error}</div>}
              </div>
              {/* Right-End: Previous paycheck  */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;
